import { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import smmApi from '../../../services/smmApi';

function currentMonth() {
  return new Date().toISOString().slice(0, 7);
}

// "2026-10" → "October 2026" (built from parts: "2026-10-01" parses as UTC and can land in September).
function monthLabel(yyyymm: string) {
  const [y, m] = yyyymm.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

const platformIcons: Record<string, string> = {
  LinkedIn: 'ri-linkedin-box-line',
  Instagram: 'ri-instagram-line',
  Twitter: 'ri-twitter-x-line',
  YouTube: 'ri-youtube-line',
  Facebook: 'ri-facebook-circle-line',
};

const priorityBadge = (p: string) => {
  if (p === 'high') return 'bg-red-50 text-red-600';
  if (p === 'medium') return 'bg-amber-50 text-amber-600';
  return 'bg-gray-50 text-gray-500';
};

const stageLabel = (s: string) => {
  if (s === 'draft') return 'Draft';
  if (s === 'production') return 'Production';
  if (s === 'review') return 'Review';
  return 'Scheduled';
};

const formatScheduledDate = (dateValue: string | null | undefined) => {
  if (!dateValue) return 'TBD';
  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.valueOf())) return String(dateValue);
  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const mapGovernanceStatusToLifecycle = (status: string): Status => {
  const s = String(status || '').toUpperCase();
  if (s === 'DONE') return 'done';
  if (s === 'POSTED' || s === 'SCHEDULED_MANUAL' || s === 'SCHEDULED') return 'posted';
  if (s === 'NOT_CREATED') return 'not-created';
  return 'not-started';
};

const alertSeverityDot = (severity: string) => {
  if (severity === 'critical') return 'bg-red-500';
  if (severity === 'warning') return 'bg-amber-500';
  if (severity === 'info') return 'bg-sky-500';
  return 'bg-gray-400';
};

const alertSeverityLabel = (severity: string) => {
  if (severity === 'critical') return 'Critical';
  if (severity === 'warning') return 'Warning';
  if (severity === 'info') return 'Info';
  return 'Deviation';
};

type Status = 'done' | 'posted' | 'not-started' | 'not-created';

export default function ExecutionGovernanceEngine() {
  const [posts, setPosts] = useState<any[]>([]);
  const [governanceSummary, setGovernanceSummary] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  // Load failure (shown with Retry) vs. no calendar for this month (404).
  const [loadError, setLoadError] = useState<string | null>(null);
  const [noPlan, setNoPlan] = useState(false);
  // The summary (totals, alerts) failed but the posts loaded: show the posts with a notice.
  const [summaryError, setSummaryError] = useState<string | null>(null);
  // Month of the calendar shown — the backend falls back to the latest one when this month has none.
  const [calendarMonth, setCalendarMonth] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const userId = localStorage.getItem('user_id') || '';
  const month = currentMonth();

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setLoadError(null);
    setNoPlan(false);
    setSummaryError(null);
    // Separately: a failed summary mustn't hide posts that loaded.
    Promise.allSettled([
      smmApi.getSmmGovernanceSummary(userId, month),
      smmApi.getSmmGovernancePosts(userId, month),
    ]).then(([summaryResult, postsResult]) => {
      if (postsResult.status === 'rejected') throw postsResult.reason;
      const postsData = postsResult.value;
      setCalendarMonth(
        postsData?.[0]?.calendar_month ||
        (summaryResult.status === 'fulfilled' ? summaryResult.value?.month : null) ||
        null
      );
      if (summaryResult.status === 'fulfilled') {
        setGovernanceSummary(summaryResult.value);
      } else {
        setGovernanceSummary(null);
        setSummaryError(summaryResult.reason?.message || "Couldn't load this month's totals.");
      }
      const mappedPosts = (postsData || []).map((post: any) => {
        // Build title: creative_copy first line > caption first line > theme & format > post number
        const rawTitle =
          (post.creative_copy || '').trim().split('\n')[0].replace(/^#+\s*/, '').trim().slice(0, 60) ||
          (post.caption || '').trim().split('\n')[0].trim().slice(0, 60) ||
          (post.theme_type && post.content_format ? `${post.theme_type} • ${post.content_format}` : '') ||
          post.theme_type ||
          post.content_format ||
          `Post #${post.post_number}`;

        return {
          id: post.post_id,
          title: rawTitle,
          platform: post.platform,
          status: mapGovernanceStatusToLifecycle(post.governance_status),
          governance_status: post.governance_status,
          has_content: post.has_content ?? true,
          scheduledDate: formatScheduledDate(post.scheduled_date),
          rawScheduledDate: post.scheduled_date,
          is_campaign_post: post.is_campaign_post,
          is_overdue: post.is_overdue,
          post_number: post.post_number,
          weekly_theme_label: post.weekly_theme_label,
          theme_type: post.theme_type,
        };
      });
      setPosts(mappedPosts);
    }).catch((err: any) => {
      setGovernanceSummary(null);
      setPosts([]);
      if (err?.response?.status === 404) {
        setNoPlan(true);
      } else {
        setLoadError(err?.message || "Couldn't load execution data.");
      }
    }).finally(() => {
      setLoading(false);
    });
  }, [userId, month, reloadKey]);

  const toggleStatus = async (id: string, targetStatus: 'done' | 'posted') => {
    const post = posts.find(p => p.id === id);
    if (!post) return;
    if (!post.has_content) return;

    // Toggle back to not-started if already selected, otherwise set to target
    const nextStatus: Status = post.status === targetStatus ? 'not-started' : targetStatus;
    const backendGovStatus =
      nextStatus === 'done'
        ? 'DONE'
        : nextStatus === 'posted'
        ? 'SCHEDULED_MANUAL'
        : 'NOT_STARTED';

    // 1. Optimistic UI update for immediate response
    setPosts(prev =>
      prev.map(p =>
        p.id === id
          ? {
              ...p,
              status: nextStatus,
              governance_status: backendGovStatus,
            }
          : p
      )
    );

    // 2. Dispatch backend update; on failure revert and tell the user
    try {
      await smmApi.updateGovernanceStatus(id, backendGovStatus);
    } catch (err: any) {
      console.error('Failed to update post status:', err);
      setPosts(prev =>
        prev.map(p =>
          p.id === id ? { ...p, status: post.status, governance_status: post.governance_status } : p
        )
      );
      toast.error(`Couldn't update "${post.title}". ${err?.message || 'Please try again.'}`);
      return;
    }

    // 3. Re-fetch summary so Completion percentage updates on same screen.
    //    The status is already saved, so a failure here must not revert it.
    if (userId) {
      try {
        const updatedSummary = await smmApi.getSmmGovernanceSummary(userId, month);
        if (updatedSummary) {
          setGovernanceSummary(updatedSummary);
        }
      } catch (err) {
        console.error('Failed to refresh governance summary:', err);
        toast.warning('Status saved, but the totals could not refresh. Reload to see the latest numbers.');
      }
    }
  };

  const stages = ['draft', 'production', 'review', 'scheduled'];

  // Operational Alert System metrics from API — null (shown "—") when the summary didn't load,
  // never 0 % / 0 overdue under the "couldn't load" notice.
  const completionRate = governanceSummary?.completion_rate ?? null;

  const overduePosts = governanceSummary?.overdue ?? null;
  const totalPending = governanceSummary?.pending ?? null;

  const campaign = governanceSummary?.campaign;
  const totalCampaigns = campaign?.total ?? 0;
  const incompleteCampaigns = campaign
    ? campaign.total - campaign.completed
    : 0;

  const operationalAlerts: any[] = governanceSummary?.operational_alerts ?? [];

  // Derive pending workload from live posts — exclude only POSTED
  const pendingWorkload = useMemo(() => {
    return posts
      .filter((p: any) => p.governance_status !== 'POSTED')
      .map((p: any) => {
        let stage: string;
        if (p.is_overdue) {
          stage = 'review';
        } else if (p.governance_status === 'IN_PROGRESS') {
          stage = 'production';
        } else if (p.governance_status === 'DONE' || p.governance_status === 'SCHEDULED_MANUAL' || p.governance_status === 'SCHEDULED') {
          stage = 'scheduled';
        } else {
          stage = 'draft';
        }
        const priority = p.is_overdue ? 'high' : p.is_campaign_post ? 'medium' : 'low';
        return {
          id: p.id,
          title: p.title,
          platform: p.platform,
          date: p.rawScheduledDate || '',
          priority,
          stage,
        };
      });
  }, [posts]);

  const campaignGovernanceChains = useMemo(() => {
    const campaignPosts = posts
      .filter((p: any) => p.is_campaign_post)
      .sort((a: any, b: any) => (a.post_number ?? 0) - (b.post_number ?? 0));

    if (!campaignPosts.length) return [];

    // Group by weekly_theme_label (campaign name)
    const groups: Record<string, any[]> = {};
    for (const p of campaignPosts) {
      const key = p.weekly_theme_label || p.theme_type || 'Campaign';
      if (!groups[key]) groups[key] = [];
      groups[key].push(p);
    }

    return Object.entries(groups).map(([name, chainPosts]) => {
      const total = chainPosts.length;
      const completed = chainPosts.filter((p: any) => p.status === 'done' || p.status === 'posted' || p.governance_status === 'DONE' || p.governance_status === 'POSTED' || p.governance_status === 'SCHEDULED_MANUAL').length;
      const breaks: { type: string; at: number }[] = [];

      // Find the highest completed post_number
      const completedNumbers = chainPosts
        .filter((p: any) => p.status === 'done' || p.status === 'posted' || p.governance_status === 'DONE' || p.governance_status === 'POSTED' || p.governance_status === 'SCHEDULED_MANUAL')
        .map((p: any) => p.post_number ?? 0);
      const maxCompleted = completedNumbers.length ? Math.max(...completedNumbers) : 0;

      chainPosts.forEach((p: any, idx: number) => {
        const pos = idx + 1;
        const isDone = p.status === 'done' || p.status === 'posted' || p.governance_status === 'DONE' || p.governance_status === 'POSTED' || p.governance_status === 'SCHEDULED_MANUAL';

        // Skipped post: not done but a later post in the chain is done
        if (!isDone && (p.post_number ?? pos) < maxCompleted) {
          breaks.push({ type: 'Broken Narrative Flow', at: pos });
          return;
        }
        // Delayed execution: overdue and not done
        if (p.is_overdue && !isDone) {
          breaks.push({ type: 'Delayed Execution', at: pos });
          return;
        }
        // Incomplete sequence: not done, not overdue, but prior posts in chain are done
        if (!isDone && completed > 0 && (p.post_number ?? pos) > 1) {
          breaks.push({ type: 'Incomplete Sequence', at: pos });
        }
      });

      return { name, total, completed, breaks };
    });
  }, [posts]);

  const observanceMisses = !governanceSummary ? null : governanceSummary.observance_misses ?? operationalAlerts
    .filter((alert) => alert.title?.toLowerCase().includes('observance'))
    .reduce((sum, alert) => {
      const match = String(alert.message || '').match(/(\d+)\s+posts?\s+missed/i);
      return sum + (match ? Number(match[1]) : 1);
    }, 0);

  if (loading) return (
    <div className="space-y-6 animate-pulse">
      {/* Operational Alert System skeleton */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <div className="h-4 w-40 bg-gray-200 rounded mb-4" />
        <div className="grid grid-cols-5 gap-4 mb-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex flex-col items-center gap-1.5">
              <div className="h-8 w-16 bg-gray-200 rounded" />
              <div className="h-3 w-20 bg-gray-100 rounded" />
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="h-14 bg-gray-100 rounded-lg" />
          ))}
        </div>
      </div>
      {/* Post Lifecycle Tracking skeleton */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <div className="h-4 w-44 bg-gray-200 rounded mb-4" />
        <div className="grid grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-32 bg-gray-100 rounded-xl" />
          ))}
        </div>
      </div>
      {/* Campaign Governance Tracking skeleton */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <div className="h-4 w-52 bg-gray-200 rounded mb-4" />
        <div className="h-20 bg-gray-100 rounded-xl" />
      </div>
      {/* Pending Workload Visibility Matrix skeleton */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <div className="h-4 w-56 bg-gray-200 rounded mb-4" />
        <div className="grid grid-cols-4 gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-48 bg-gray-100 rounded-xl" />
          ))}
        </div>
      </div>
    </div>
  );

  // No user, a failed load, or no calendar this month: say so instead of showing zeros.
  if (!userId || loadError || noPlan) {
    const isError = Boolean(loadError) || !userId;
    return (
      <div className={`flex items-start gap-3 p-5 rounded-xl border ${isError ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
        <i className={`${isError ? 'ri-error-warning-line text-red-500' : 'ri-information-line text-amber-600'} text-lg leading-none mt-0.5`} />
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-semibold ${isError ? 'text-red-800' : 'text-amber-800'}`}>
            {!userId
              ? "We couldn't identify your account. Please sign out and sign in again."
              : loadError
                ? "Couldn't load execution tracking."
                : 'No content plan for this month yet — create one in Content Planning Engine.'}
          </p>
          {loadError && <p className="text-xs text-red-700 mt-0.5">{loadError}</p>}
        </div>
        {loadError && (
          <button
            onClick={() => setReloadKey(k => k + 1)}
            className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-white border border-red-200 text-red-700 hover:bg-red-100 transition-colors whitespace-nowrap"
          >
            Retry
          </button>
        )}
      </div>
    );
  }

  // "—" for a total the summary didn't provide.
  const fmtTotal = (v: number | null) => (v == null ? '—' : v);

  return (
    <div className="space-y-6">
      {calendarMonth && calendarMonth !== month && (
        <div className="flex items-center gap-3 p-3 rounded-lg border bg-amber-50 border-amber-200">
          <i className="ri-information-line text-amber-600 text-base leading-none" />
          <p className="flex-1 text-xs text-amber-800">
            Showing {monthLabel(calendarMonth)} — no content plan for {monthLabel(month)} yet.
          </p>
        </div>
      )}
      {summaryError && !loading && (
        <div className="flex items-center gap-3 p-3 rounded-lg border bg-amber-50 border-amber-200">
          <i className="ri-error-warning-line text-amber-600 text-base leading-none" />
          <p className="flex-1 text-xs text-amber-800">
            Couldn't load this month's totals and alerts ({summaryError}). The post list below is up to date.
          </p>
          <button
            onClick={() => setReloadKey(k => k + 1)}
            className="px-3 py-1 text-xs font-semibold rounded-lg bg-white border border-amber-200 text-amber-800 hover:bg-amber-100 transition-colors whitespace-nowrap"
          >
            Retry
          </button>
        </div>
      )}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      )}
      {!loading && (<>
        {/* Operational Alert System */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <h3 className="font-semibold text-gray-900 text-sm mb-4">Operational Alert System</h3>
          <div className="grid grid-cols-5 gap-4 mb-5">
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">{completionRate !== null ? `${Math.round(Number(completionRate))}%` : '—'}</p>
              <p className="text-[11px] text-gray-500 mt-0.5">Completion</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">
                {!governanceSummary ? '—' : totalCampaigns > 0 ? `${incompleteCampaigns}/${totalCampaigns}` : 'No campaigns'}
              </p>
              <p className="text-[11px] text-gray-500 mt-0.5">
                {!governanceSummary || totalCampaigns > 0 ? 'Incomplete Campaigns' : 'No campaigns selected this month'}
              </p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-red-600">{fmtTotal(overduePosts)}</p>
              <p className="text-[11px] text-gray-500 mt-0.5">Overdue Posts</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-amber-600">{fmtTotal(observanceMisses)}</p>
              <p className="text-[11px] text-gray-500 mt-0.5">Observance misses</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">{fmtTotal(totalPending)}</p>
              <p className="text-[11px] text-gray-500 mt-0.5">Pending Workload</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {operationalAlerts.length === 0 && (
              <div className="col-span-2 text-center py-4 text-[11px] text-gray-400 italic">
                {governanceSummary ? 'No active operational alerts.' : "Couldn't load this month's alerts."}
              </div>
            )}
            {operationalAlerts.map((alert: any) => (
              <div key={alert.id} className="flex items-start gap-3 p-3 rounded-lg border border-gray-100 bg-gray-50/50">
                <span className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${alertSeverityDot(alert.severity)}`} />
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-xs font-semibold text-gray-900">{alert.title}</span>
                    <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-white border border-gray-200 text-gray-500">
                      {alertSeverityLabel(alert.severity)}
                    </span>
                  </div>
                  <p className="text-[11px] text-gray-500 leading-relaxed">{alert.message}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Post Lifecycle Tracking */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <h3 className="font-semibold text-gray-900 text-sm mb-4">Post Lifecycle Tracking</h3>
          <div className="grid grid-cols-3 gap-4">
            {posts.map(post => (
              <div
                key={post.id}
                className={`p-4 rounded-xl border transition-colors ${
                  !post.has_content
                    ? 'bg-gray-50/80 border-gray-200/60 opacity-75'
                    : post.status === 'done'
                      ? 'bg-emerald-50/50 border-emerald-200/60'
                      : post.status === 'posted'
                        ? 'bg-sky-50/50 border-sky-200/60'
                        : 'bg-white border-gray-200'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <i
                      className={`${platformIcons[post.platform] || 'ri-global-line'} text-sm w-4 h-4 flex items-center justify-center text-gray-600`}
                    />
                    <span className="text-xs font-medium text-gray-500">{post.platform}</span>
                  </div>
                  <span
                    className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                      !post.has_content
                        ? 'bg-amber-100 text-amber-700'
                        : post.status === 'done'
                          ? 'bg-emerald-100 text-emerald-700'
                          : post.status === 'posted'
                            ? 'bg-sky-100 text-sky-700'
                            : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {!post.has_content ? 'Not Created' : post.status === 'done' ? 'Done' : post.status === 'posted' ? 'Posted' : 'Pending'}
                  </span>
                </div>
                <p className="text-sm font-medium text-gray-900 mb-1 line-clamp-2">{post.title}</p>
                <p className="text-xs text-gray-500 mb-3">Scheduled: {post.scheduledDate}</p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => toggleStatus(post.id, 'done')}
                    disabled={!post.has_content}
                    className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-[11px] font-medium transition-colors whitespace-nowrap ${
                      !post.has_content
                        ? 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed'
                        : post.status === 'done'
                          ? 'bg-emerald-500 text-white shadow-sm'
                          : 'bg-gray-50 text-gray-600 hover:bg-emerald-50 hover:text-emerald-700 border border-gray-200'
                    }`}
                  >
                    <span className={`w-2 h-2 rounded-sm ${!post.has_content ? 'bg-gray-400' : post.status === 'done' ? 'bg-white' : 'bg-emerald-500'}`} />
                    Done
                  </button>
                  <button
                    onClick={() => toggleStatus(post.id, 'posted')}
                    disabled={!post.has_content}
                    className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-[11px] font-medium transition-colors whitespace-nowrap ${
                      !post.has_content
                        ? 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed'
                        : post.status === 'posted'
                          ? 'bg-sky-500 text-white shadow-sm'
                          : 'bg-gray-50 text-gray-600 hover:bg-sky-50 hover:text-sky-700 border border-gray-200'
                    }`}
                  >
                    <span className={`w-2 h-2 rounded-sm ${!post.has_content ? 'bg-gray-400' : post.status === 'posted' ? 'bg-white' : 'bg-sky-500'}`} />
                    Posted
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Campaign Governance Tracking */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <h3 className="font-semibold text-gray-900 text-sm mb-4">Campaign Governance Tracking</h3>
          {campaignGovernanceChains.length === 0 && (
            <div className="text-center py-8 text-[11px] text-gray-400 italic bg-gray-50 rounded-xl border border-dashed border-gray-200">
              No campaign posts found for this month.
            </div>
          )}
          <div className="space-y-8">
            {campaignGovernanceChains.map(camp => (
              <div key={camp.name}>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{camp.name}</p>
                    <p className="text-[11px] text-gray-500">
                      {camp.completed} of {camp.total} posts completed
                    </p>
                  </div>
                  <span
                    className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${camp.breaks.length === 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
                      }`}
                  >
                    {camp.breaks.length === 0 ? 'Healthy' : `${camp.breaks.length} Issue${camp.breaks.length > 1 ? 's' : ''}`}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  {Array.from({ length: camp.total }).map((_, i) => {
                    const isDone = i < camp.completed;
                    const breakAt = camp.breaks.find(b => b.at === i + 1);
                    return (
                      <div key={i} className="flex items-center gap-1.5 flex-1">
                        <div
                          className={`flex-1 h-10 rounded-lg border flex items-center justify-center relative ${isDone
                              ? 'bg-emerald-50 border-emerald-200'
                              : breakAt
                                ? 'bg-red-50 border-red-200'
                                : 'bg-gray-50 border-gray-200'
                            }`}
                        >
                          <span
                            className={`text-[10px] font-medium ${isDone ? 'text-emerald-700 font-bold' : breakAt ? 'text-red-700' : 'text-gray-500'
                              }`}
                          >
                            {isDone ? '✓' : breakAt ? '!' : i + 1}
                          </span>
                          {breakAt && (
                            <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 whitespace-nowrap">
                              <span className="text-[9px] text-red-600 font-medium">{breakAt.type}</span>
                            </div>
                          )}
                        </div>
                        {i < camp.total - 1 && (
                          <div
                            className={`w-3 h-0.5 rounded-full ${isDone && i < camp.completed - 1 ? 'bg-emerald-300' : breakAt ? 'bg-red-300' : 'bg-gray-200'
                              }`}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pending Workload Visibility Matrix */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <h3 className="font-semibold text-gray-900 text-sm mb-4">Pending Workload Visibility Matrix</h3>
          <div className="grid grid-cols-4 gap-3">
            {stages.map(stage => {
              const items = pendingWorkload.filter(w => w.stage === stage);
              return (
                <div key={stage} className="bg-gray-50/50 rounded-xl border border-gray-100 p-3">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-semibold text-gray-700 capitalize">{stageLabel(stage)}</span>
                    <span className="text-[10px] font-bold text-gray-500 bg-white px-1.5 py-0.5 rounded-md border border-gray-200">
                      {items.length}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {items.map(item => (
                      <div key={item.id} className="bg-white rounded-lg border border-gray-200 p-2.5 shadow-sm">
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <i
                            className={`${platformIcons[item.platform] || 'ri-global-line'} text-xs w-3 h-3 flex items-center justify-center text-gray-500`}
                          />
                          <span className="text-[10px] text-gray-500">{item.platform}</span>
                        </div>
                        <p className="text-xs font-medium text-gray-900 mb-1.5 line-clamp-2">{item.title}</p>
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] text-gray-400">{item.date.slice(5)}</span>
                          <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${priorityBadge(item.priority)}`}>
                            {item.priority}
                          </span>
                        </div>
                      </div>
                    ))}
                    {items.length === 0 && (
                      <div className="text-center py-4">
                        <span className="text-[10px] text-gray-400">No items</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </>)}
    </div>
  );
}
